<template>
    <l-layer-group
        :layer-type="overlay.type"
        v-model:visible="overlay_visible"
        :name="(overlay.name)"
        ref="smartSearchTracksLayer"
        @ready="onReady()"
    >
        <l-feature-group
            ref="smartSearchTrackCluster"
            id="smart-search-track-cluster"
        ></l-feature-group>
        <l-feature-group
            ref="smartSearchTrackPathLayer"
            id="smart-search-track-path-layer"
        ></l-feature-group>
    </l-layer-group>
</template>

<script>
import {LFeatureGroup, LLayerGroup} from "@vue-leaflet/vue-leaflet";
import {mapGetters} from "vuex";
import {toRaw} from "vue";
import debounce from "lodash/debounce";
import {buildIconObject} from "@/lib/lib";

const validPictures = Object.freeze(
    [
        'map__pin-event',
        'map__pin-a-point',
        'map__pin-b-point',
        'map__pin-lost-connection',
        'map__pin-low-battery',
        'map__pin-max-speed',
        'map__pin-parking',
        'map__pin-shock',
        'map__pin-stop',
        'map__pin-turn',
        'map__pin-smart-search.loading',
        'map__pin-smart-search.done',
        'map__pin-smart-search-accident.done',
        'map__pin-smart-search-accident.loading',
        'map__pin-insurance-report',
        'map__pin-accident.done',
        'map__pin-accident.loading',
    ])

export default {
    name: "SectionMap_layer_smart_search_tracks",
    emits: ['update:modelValue'],
    mixins: [],
    inject: ['L', 'createMarker', 'mapLeafletObject', 'toast'],
    props: {
        modelValue: Object,
    },
    components: {
        LLayerGroup,
        LFeatureGroup,
    },
    data() {
        return {
            parkedMarkers: [],
            trackControlPointsLayerCluster: null,
            smartSearchTracksLayer: null,
            trackEventsPointsGroup: [],
            trackLayers: null,
            trackMarkers: [],
        }
    },
    computed: {
        ...mapGetters([
            // "getAppUserSettings",
            "getAccidents",
            "getAccidentsByIds",
            "getTracks",
            "getParkings",
            "getStops",
            "getEvents",
            "getVideoEvents",
            "getVideoRequests",
            "getVideoEventsI18nMessagesByXid",
        ]),
        overlay() {
            return this.modelValue
        },
        unitId() {
            return this.routeFilter.unitId
        },
        timeFrom() {
            return this.routeFilter.from
        },
        timeTo() {
            return this.routeFilter.to
        },
        accidentId() {
            return this.routeFilter.accidentId
        },
        hideEvents() {
            return this.routeQueryFilter.hideEvents
        },
        eventMarkerOnTrack() {
            return {type: this.routeFilter.trackEventType, xid: this.routeFilter.trackEventXid }
        },
        overlay_visible: {
            get() {
                return this.overlay?.visible
            },
            set(vl) {
                this.$emit('update:modelValue', {...this.overlay, visible: vl})
            },
        },
        shortAccidents() {
            return this.getAccidents.map(a => a.shot_media_request_id)
        },
        videoRequests() {
            let unitId = this.unitId
            let timeFrom = this.timeFrom
            let timeTo = this.timeTo

            if (!timeFrom || !timeTo) return null
            let accident = this.getAccidentsByIds[this.accidentId]

            return this.getVideoRequests
                .filter(r => {
                    return r.unit_id == unitId && timeFrom <= r?.time?.utc
                        && r.time.utc <= timeTo
                        && ['smart-search', 'smart-search-accident', 'accident'].includes(r['sub-type']?.split('.')[0])
                        && !this.shortAccidents.includes(r.media_request_id)
                        && (this.accidentId ? accident?.media_request_id == r?.media_request_id : true)
                })
                .map(r => {
                    let message = this.getVideoEventsI18nMessagesByXid[r.xid] || r.text || '';
                    let parking = this.getParkingsFiltered?.find((p) => {
                        return p?.time_start?.utc - (60*2) <= r.time.utc && p?.time_start?.utc >= r?.time?.utc;
                    })
                    return {...r, message, latlng: parking?.coord_start || r?.latlng}
                });
        },
        updateVideoRequests() {
            return !this.track ? 0 : this.videoRequests.map(v => v['sub-type'])?.join(',')
        },
        getParkingsFiltered() {
            let unitId = this.unitId
            let timeFrom = this.timeFrom
            let timeTo = this.timeTo
            return this.getParkings
                .filter(p => p.unit_id == unitId &&
                    timeFrom <= p?.time_end?.utc &&
                    timeTo >= p.time_start?.utc
                )
        },
        track() {
            let unitId = this.unitId
            let timeFrom = this.timeFrom
            let timeTo = this.timeTo

            if (!timeFrom || !timeTo) return null

            let track = this.getTracks
                .filter(t => {
                    return t.unit_id == unitId && timeFrom <= t.timeTo && timeTo >= t.timeFrom
                })
            if (!track?.length) return null;
            track.sort((a, b) => a.timeFrom - b.timeFrom)
            return track.reduce((out, t) => {
                if (!out.trackObjects) out.trackObjects = [];
                if (!out.msgs) out.msgs = [];

                out.date = ''
                out.controlPoints = []
                out.unit_id = unitId
                out.distance = (t?.distance || 0) + (out?.distance || 0);
                out.maxSpeed = t?.maxSpeed > out?.maxSpeed ? t?.maxSpeed : (out?.maxSpeed || '');
                out.timeFrom = timeFrom;
                out.timeTo = timeTo;
                out.trackObjects = [...out.trackObjects, ...t.trackObjects].filter(v => v)
                out.msgs = [...out.msgs, ...t.msgs].filter(v => v)
                return out;
            }, {})
        },
        trackLatLngs() {
            return this.track ? this.track.msgs.map(msg => toRaw(msg.latlng)) : []
        },
        showUnitTrackColor(){
           return !!this.routeFilter.trackColor
        },
    },
    watch: {
        track: {
            handler: function (newTrack, oldTrack) {
                if (oldTrack || newTrack) {
                    // if(this.$isDevelopment) console.log('track', newTrack, oldTrack)
                }
                this.debouncedUpdateUnitTrack();
                this.debouncedUpdateFitBounds();
            },
            //deep: true
        },
        getParkingsFiltered: {
            handler: function (newTrack, oldTrack) {
                if (oldTrack || newTrack) {
                    // if(this.$isDevelopment) console.log('track', newTrack, oldTrack)
                }
                this.debouncedUpdateUnitParked();
                this.debouncedUpdateFitBounds();
            },
        },
        updateVideoRequests: {
            handler: function (newRequest, oldRequests) {
                this.debouncedUpdateVideoRequestsMarkers();
                if (newRequest && !oldRequests) {
                    this.debouncedUpdateFitBounds()
                }
            },
        },
        // showUnitTrackColor: {
        //     handler: function () {
        //         this.debouncedUpdateSpeedActive()
        //     },
        //     //deep: true
        // },
        eventMarkerOnTrack: {
            handler: function (newVal, oldValue) {
                if ((newVal?.type != oldValue?.type) || (newVal?.xid != oldValue?.xid)) {
                    let group = this.trackEventsPointsGroup.filter(gr => gr.type == 'control-' + newVal.type)
                    if (!group.length || group.length > 1) {
                        console.warn('trackEventsPointsGroup - ', group)
                        return
                    }
                    let marker = group[0].layers.find(m => m.meta.xid == newVal.xid)
                    if (!marker) {
                        console.warn('trackEventsPointsGroup.layers.marker - not found')
                        return
                    }
                    setTimeout(() => marker.openPopup(), 500)
                }
            },
            deep: true
        },
    },
    methods: {
        createTrack(id, messages) {
            let trackLayers = [];

            let options = {
                color: '#007aff',
                opacity: 1,
                noClip: true,
                smoothFactor: 3,
                weight: 5,
            }

            let latlngs = messages.map(m => m.latlng)

            let trackPoly = new this.L.Polyline(latlngs, options)
            trackLayers.push(trackPoly);//ToDo ???

            if (!trackLayers.length) {
                return false;
            }

            let track = {
                layer: new this.L.FeatureGroup(trackLayers),
                id: id
            };

            return track
        },
        iconPoint(image, type) {
            return buildIconObject(image, type)
        },
        async createTrackPoints(points, id, type/*, hideTooltips = false*/) {
            let pointsMarkers = [];

            points = points
                .filter(point => {
                    if (!point.latlng) {
                        console.warn('createTrackPoints - no latlng', {latlng: null, ...point})
                    }
                    return point.latlng
                })
                .map(async point => {
                    let iconName = this.iconPoint(point.image)
                    iconName = 'map__pin-' + iconName.name
                    let options = {
                        icon: {
                            assetPath: '/img/track/' + (validPictures.includes(iconName) ? iconName : 'map__pin-warning') + '.png',
                            anchorK: [0.5, 1],
                        },
                        popup: {
                            anchorK: [0, -1]
                        },
                        tooltip: {
                            anchorK: [0, -1]
                        },
                        zIndexOffset: 100,
                        meta: {
                            timestamp: point.time.utc,
                            duration: point.duration,
                            time: point.time.formated['time-s'],
                            // timeFrom: el.time_start.utc,
                            // timeTo: el.time_end.utc,
                            eventType: point.type,
                            xid: point.xid,
                            type: point['sub-type'] ? point.type + '.' + point['sub-type'] : point.type,
                            label: '' + point.time.formated['time-s'] + ' / ' + (point.message || point.text),
                        }
                    };
                    return await this.createMarker(point.latlng, options)
                })

            Promise.all(points)
                .then(markers => {
                    markers
                        .filter(marker => !!marker)
                        .forEach(marker => {
                            marker.on('click', (e) => {
                                // if(this.$isDevelopment) console.log(this.eventMarkerOnTrack)
                                let xid = e.target.meta.xid
                                let isBigVideo = (+e?.target?.meta?.duration >= 120)
                                if(isBigVideo) {
                                    this.setRoute['video-request'](this.$route, this.$router, this.unitId, xid)
                                } else {
                                    this.setRoute.smartSearchRequest(this.$route, this.$router, 'time', this.unitId, xid)
                                }
                            })
                        })

                    pointsMarkers.push(...markers)
                    let pointsMarkersObj = {
                        layers: pointsMarkers,
                        type: 'control' + '-' + type,
                        id: id
                    }
                    this.trackEventsPointsGroup.push(pointsMarkersObj);
                    if (this.trackControlPointsLayerCluster) {
                        // this.trackControlPointsLayerCluster.addLayers(pointsMarkers);
                        pointsMarkers.forEach((marker) => {
                            this.trackControlPointsLayerCluster.addLayer(marker)
                        })
                    }

                })
        },
        async updateUnitTrack() {
            // if(this.$isDevelopment) console.log('updateUnitTrack', this.track)
            if (this.trackLayer) {//remove markers
                this.smartSearchTrackPathLayer.removeLayer(this.trackLayer)
                this.trackLayer = null

                this.trackMarkers.forEach((marker) => {
                    this.smartSearchTrackPathLayer.removeLayer(marker)
                })
                this.trackMarkers = []

                this.trackEventsPointsGroup.forEach((g) => {
                    //this.trackControlPointsLayerCluster.removeLayers(g.layers);
                    [...g.layers].forEach((layer) => {
                        this.trackControlPointsLayerCluster.removeLayer(layer)
                    })
                })
                this.trackEventsPointsGroup = []
            }
            if (!this.track) return false;
            let track = {...this.track}

            //let detail = false//track.params.detail
            if (!track?.msgs?.length) {
                this.toast?.error(this.$t("errors.No any {name} are available in this time frame", {name: this.$t('text.tracks')}))
                return false;
            }

            // let latlngs = track.msgs.map(msg => (toRaw(msg.latlng)));
            // this.mapLeafletObject.fitBounds((latlngs), {
            //     paddingTopLeft: [100, 100],
            //     paddingBottomRight: [100, 200],
            //     // padding: [50, 50]
            // })

            let _track = this.createTrack(track.unit_id, track.msgs);
            if (_track === false) {
                this.toast.error(this.$t("Not enough points for the track"));
                return false;
            }

            this.trackLayer = _track.layer
            // this.updateUnitSpeedColor()
            //this.mapLeafletObject.fitBounds(track.layer.getBounds());
            this.smartSearchTrackPathLayer.addLayer(_track.layer)

            // create A B marker
            if (track?.msgs?.length > 1) {
                let marker, msg, options

                //start A
                msg = track.msgs[0]
                options = {
                    icon: {
                        assetPath: '/img/track/map__pin-a-point.png',
                        anchorK: [0.5, 1],
                    },
                }
                marker = await this.createMarker(msg.latlng, options)
                if (marker) {
                    marker.meta = {
                        sUniqueKey: track.unit_id + '_start'
                    }
                    marker.addTo(this.smartSearchTrackPathLayer)
                    this.trackMarkers.push(marker)
                }

                //finish B
                msg = track.msgs[track.msgs.length - 1]
                options = {
                    icon: {
                        assetPath: '/img/track/map__pin-b-point.png',
                        anchorK: [0.5, 1],
                    },
                }
                marker = await this.createMarker(msg.latlng, options)
                if (marker) {
                    marker.meta = {
                        sUniqueKey: track.unit_id + '_finish'
                    }
                    marker.addTo(this.smartSearchTrackPathLayer)
                    this.trackMarkers.push(marker)
                }

                this.trackMarkers.forEach(m => {
                    m.on('click', () => {
                        let paddingBounds = this.getMapPaddingBounds || this.$consts.components.map.paddingBounds
                        this.mapLeafletObject.fitBounds(this.trackLatLngs, paddingBounds)
                    })
                })
            }
        },
        // updateUnitSpeedColor() {
        //     if (this.trackLayer) {
        //         if (this.showUnitTrackColor) {
        //             this.trackLayer.eachLayer(function (layer) {
        //                 if (layer.getElement && typeof layer.getElement == 'function') {
        //                     let classList = layer.getElement()?.classList
        //                     if (classList) {
        //                         layer.getElement().classList.remove('track-layer-color-none')
        //                         layer.getElement().classList.add('track-layer-color')
        //                     }
        //                 }
        //             });
        //         } else {
        //             this.trackLayer.eachLayer(function (layer) {
        //                 if (layer.getElement && typeof layer.getElement == 'function') {
        //                     let classList = layer.getElement()?.classList
        //                     if (classList) {
        //                         layer.getElement().classList.add('track-layer-color-none')
        //                         layer.getElement().classList.remove('track-layer-color')
        //                     }
        //                 }
        //             });
        //         }
        //     }
        // },
        async updateUnitParked() {
            // if(this.$isDevelopment) console.log('updateUnitParked', this.getParkingsFiltered)
            if (this.parkedMarkers.length) {//remove markers
                this.parkedMarkers.forEach(m => this.smartSearchTrackPathLayer.removeLayer(m))
                this.parkedMarkers = []
            }

            if(!this.getParkingsFiltered?.length) return false;

            let parkings = this.getParkingsFiltered
                .filter(p => p?.coord_start?.length)

            for (let i = 0; i < parkings.length; i++) {
                let p = parkings[i];

                let options = {
                    icon: {
                        assetPath: '/img/track/map__pin-parking.png',
                        anchorK: [0.5, 1],
                    },
                    popup: {
                        text: '',
                        anchorK: [0, -1]
                    },
                    meta: {
                        sUniqueKey: p.unit_id + '_parking',
                        popup: {
                            content: this.$t('popup.{time} - {event}: {duration}', {
                                time: p.time_start.formated['time-s'],
                                event: this.$t('text.Parking'),
                                duration: p.duration.formated
                            }),
                            className: 'startFinish-popup'
                        },
                    }
                }

                let marker = await this.createMarker(p.coord_start, options)
                if (marker) {
                    if (marker?.meta?.popup) {
                        marker.bindPopup(marker.meta.popup?.content, {className: marker.meta.popup?.className});
                    }
                    this.parkedMarkers.push(marker)
                    this.smartSearchTrackPathLayer.addLayer(marker)
                }
            }
        },
        async updateEventsMarkers(type, events){
            this.trackEventsPointsGroup.forEach((g) => {
                //this.trackControlPointsLayerCluster.removeLayers(g.layers);
                [...g.layers].forEach((layer) => {
                    this.trackControlPointsLayerCluster.removeLayer(layer)
                })
            })
            this.trackEventsPointsGroup = []

            if (events?.length && this.unitId && !this.hideEvents) {
                await this.createTrackPoints(events, this.unitId, type, true)
            }
        },
        updateFitBounds() {
            let trackBounds = this.smartSearchTrackPathLayer.getBounds();
            let pointsLatLngs = this.trackControlPointsLayerCluster.getBounds();
            let latLngs = [];
            if(trackBounds?._northEast && trackBounds?._southWest) latLngs.push(trackBounds);
            if(pointsLatLngs?._northEast && pointsLatLngs?._southWest) latLngs.push(pointsLatLngs);

            if(latLngs.length) {
                let paddingBounds = this.getMapPaddingBounds || this.$consts.components.map.paddingBounds
                this.mapLeafletObject.fitBounds([trackBounds], paddingBounds)
            }
        },
        onReady() {
            this.trackControlPointsLayerCluster = // new MarkerClusterGroup(this.smartSearchTrackClusterOptions);
                this.$refs.smartSearchTrackCluster.leafletObject
            this.smartSearchTracksLayer = this.$refs.smartSearchTracksLayer.leafletObject
            this.smartSearchTrackPathLayer = this.$refs.smartSearchTrackPathLayer.leafletObject
        },
    },
    created() {
        this.debouncedUpdateUnitParked = debounce(() => {
            this.updateUnitParked()
        }, 300);
        this.debouncedUpdateVideoRequestsMarkers = debounce(() => {
            this.updateEventsMarkers('video-requests', this.videoRequests)
        }, 300)
        this.debouncedUpdateUnitTrack = debounce(() => {
            this.updateUnitTrack()
        }, 300);
        // this.debouncedUpdateSpeedActive = debounce(() => {
        //     this.updateUnitSpeedColor()
        // }, 300);
        this.debouncedUpdateFitBounds = debounce(() => {
            this.updateFitBounds()
        }, 1000);
    },
    mounted() {
    },
    updated() {
    }
}
</script>

<style scoped>

</style>